import React, { useState, FC } from 'react';
import cls from 'classnames';
import { TextEntryXlProps } from './TextEntryXl.types';
import { getClassNames, getRootClassListFromProps } from '../../../utilities/classNames';
import MaskedInput from 'react-text-mask';
import ButtonIcon from '../../button/buttonIcon';
import { EyeIcon, EyeSlashIcon } from '@stashinvest/react-assets';
import maskDict from '../primitives/inputMasks';
import AssistiveText from '../primitives/assistiveText';

export const classNames = getClassNames('sds-text-entry-xl', [
	'has-error',
	'size-large',
	'currency-prefix',
	'hide-currency-prefix',
	'input-container',
	'input-decorator',
	'input-spacer',
]);

const TextEntryXl: FC<TextEntryXlProps> = (props) => {
	const {
		className,
		errorMessage,
		value,
		fieldType = 'simple',
		inputType = 'shortText',
		type = 'text',
		onConceal,
		onChange,
		currencyMaskOptions,
		size = 'default',
		...rest
	} = props;

	// determine input component (masked or regular)
	const inputMask = inputType ? maskDict(inputType, currencyMaskOptions) : undefined;

	// determine if input conceal icon should render
	const isSensitiveField = fieldType === 'sensitive' || type === 'password';

	const isSizeLarge = size === 'large';

	// determine if input should be concealed or not
	const [concealInput, setConcealInput] = useState(isSensitiveField);
	function handleConcealClick() {
		setConcealInput(!concealInput);
		if (typeof onConceal === 'function') onConceal(!concealInput);
	}

	const assistiveTextId = (errorMessage && `${props.id}-hint`) || 'text-entry-id';
	const commonInputProps = {
		type: isSensitiveField ? (concealInput ? 'password' : 'text') : type,
		value: value,
		onChange: onChange,
		placeholder: props.placeholder ?? props.label,
		...(errorMessage && { ['aria-invalid']: true }),
		...(errorMessage && { ['aria-describedby']: assistiveTextId }),
		...rest,
	};

	const [showPrefix, setShowPrefix] = useState(
		inputType === 'currency' && (value === '' || value === null || value === undefined)
	);

	return (
		<div
			className={cls(getRootClassListFromProps(classNames, props), className)}
			data-testid={props['data-testid']}
		>
			<label
				className={cls({
					hasError: errorMessage,
					sensitive: isSensitiveField,
					sizeLarge: isSizeLarge,
				})}
			>
				{showPrefix && (
					<div className={classNames.inputDecorator}>
						<div className={classNames.inputSpacer}>
							{isSizeLarge && <>{props.placeholder}</>}
						</div>
					</div>
				)}
				{inputMask ? (
					<MaskedInput
						{...commonInputProps}
						showMask
						guide={false}
						mask={inputMask}
						value={value}
						onChange={(e) => {
							const { value: targetValue } = e.target;
			
							setShowPrefix(
								inputType === 'currency' &&
									(targetValue === '' ||
										targetValue === null ||
										targetValue === undefined)
							);

							if (onChange) onChange(e);
						}}
					/>
				) : (
					<input {...commonInputProps} />
				)}

				{Boolean(isSensitiveField) ? (
					<ButtonIcon
						onClick={handleConcealClick}
						Icon={concealInput ? EyeIcon : EyeSlashIcon}
					/>
				) : null}
			</label>

			{errorMessage && (
				<AssistiveText id={assistiveTextId} variant="error" text={errorMessage} />
			)}
		</div>
	);
};

TextEntryXl.displayName = 'TextEntryXl';

export default TextEntryXl;
